import { useCallback } from 'react';
import { Brands } from 'models/brands';
import { Countries } from 'models/countries';
import { Marketplaces } from 'models/marketplaces';
import { Tenant } from 'models/tenant';
import { TenantData } from 'models/tenants';
import { Categories } from 'models/categories';
import { FulfillmentMethods } from 'models/fulfillmentMethods';
import { Genders } from 'models/genders';
import { Seasons } from 'models/seasons';
import { api } from 'config/api';
import { AgeGroups } from 'models/ageGroups';
import { UserFilterSet } from 'models/filterSet';
import { ReleaseNotesMonth } from 'models/releaseNotes';
import { useAuthenticatedApiCall } from './api.service';

const baseConfig = {
  baseURL: api.API_URL,
};

function useRestApiService<ResourceType>(resourceName: string) {
  const { makeAuthenticatedApiCall } = useAuthenticatedApiCall(baseConfig);

  const getAll = useCallback(async () => {
    const { data, error } = await makeAuthenticatedApiCall<ResourceType>({
      url: resourceName,
      method: 'GET',
    });
    return { data, error };
  }, [makeAuthenticatedApiCall, resourceName]);

  return {
    getAll,
  };
}

export const useBrandDataService = () => useRestApiService<Brands>('brands');
export const useCategoryDataService = () => useRestApiService<Categories>('categories');
export const useGenderDataService = () => useRestApiService<Genders>('genders');
export const useAgeGroupDataService = () => useRestApiService<AgeGroups>('age-groups');
export const useSeasonDataService = () => useRestApiService<Seasons>('seasons');
export const useCountryDataService = () => useRestApiService<Countries>('countries');
export const useMarketplacesDataService = () => useRestApiService<Marketplaces>('marketplaces');
export const useFulfillmentMethodsDataService = () => useRestApiService<FulfillmentMethods>('fulfillment-methods');
export const useTenantDataService = () => useRestApiService<Tenant>('tenants/current');
export const useTenantList = () => useRestApiService<TenantData[]>('tenants/data');
export const useServerTranslations = (locale: string) =>
  useRestApiService<Record<string, string>>(`translations/${locale}`);

export function useUserFilterSetService() {
  const { makeAuthenticatedApiCall } = useAuthenticatedApiCall(baseConfig);

  const getSets = useCallback(async () => {
    const { data, error } = await makeAuthenticatedApiCall<UserFilterSet[]>({
      url: 'user/filterSets',
      method: 'GET',
    });
    return { data, error };
  }, [makeAuthenticatedApiCall]);

  const saveSet = useCallback(
    async (content: UserFilterSet) => {
      const backendFilterSet = {
        name: content.name,
        filterSetId: content.filterSetId,
        content: JSON.stringify(content.content),
        version: content.version,
      };
      const { data, error } = await makeAuthenticatedApiCall<UserFilterSet>({
        url: 'user/filterSet',
        method: 'POST',
        data: JSON.stringify(backendFilterSet),
      });
      return { data, error };
    },
    [makeAuthenticatedApiCall]
  );

  const updateSet = useCallback(
    async (content: UserFilterSet) => {
      const backendFilterSet = {
        name: content.name,
        filterSetId: content.filterSetId,
        content: JSON.stringify(content.content),
        version: content.version,
      };
      const { data, error } = await makeAuthenticatedApiCall<UserFilterSet>({
        url: 'user/filterSet',
        method: 'PUT',
        data: JSON.stringify(backendFilterSet),
      });
      return { data, error };
    },
    [makeAuthenticatedApiCall]
  );

  const deleteSet = useCallback(
    async (filterSetId: string) => {
      const { data, error } = await makeAuthenticatedApiCall<void>({
        url: `user/filterSet/${filterSetId}`,
        method: 'DELETE',
      });
      return { data, error };
    },
    [makeAuthenticatedApiCall]
  );

  return {
    getSets,
    saveSet,
    updateSet,
    deleteSet,
  };
}

export function useReleaseNotesService() {
  const { makeAuthenticatedApiCall } = useAuthenticatedApiCall(baseConfig);

  const getReleaseNotes = useCallback(
    async (month: string) => {
      const { data, error } = await makeAuthenticatedApiCall<ReleaseNotesMonth>({
        url: 'release-notes',
        method: 'GET',
        params: {
          month,
        },
      });
      return { data, error };
    },
    [makeAuthenticatedApiCall]
  );

  return {
    getReleaseNotes,
  };
}
